import {
  createApi,
  type FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react';
import { RecordResponse } from '@/models/ServiceTypes';
import { Role } from '@/models/AccountEnums';
import type {
  AdvisorBulkEditAccountsRequest,
  DataFeedActivityRequest,
  FirmSetupAccount,
  FirmSetupAccountsRequest,
  SetupAccount,
} from '@/modules/dataFeeds/types';
import { buildSetupAccountsQueryParams } from '@/modules/dataFeeds/utils/buildSetupAccountsQueryParams';
import { dataFeedsApi } from './dataFeedsApi';
import { waServicesQuery } from './baseQueries';
import type { WARootState } from './store';

function setupAccountsTagId(args: FirmSetupAccountsRequest): string {
  return JSON.stringify(args);
}

/** Accepts the invalidation action; narrower than the store's full dispatch. */
type InvalidateFeedsDispatch = (
  action: ReturnType<typeof dataFeedsApi.util.invalidateTags>,
) => void;

type FeedListTagId = 'ADVISOR_LIST' | 'LIST';

type SetupAccountWriteResult = Promise<{ data: undefined | boolean | null }>;

/**
 * Feed cards carry per-feed account totals and assignment percentages, so
 * editing or closing accounts here changes what the Data Feeds tab shows.
 * Those lists live in a separate api, and `invalidatesTags` cannot reach
 * across one, so DataFeeds and HomeInstitutions tags are invalidated by hand
 * once the write succeeds.
 */
async function refreshFeedSummaries(
  dispatch: InvalidateFeedsDispatch,
  queryFulfilled: SetupAccountWriteResult,
  feedListId: FeedListTagId,
): Promise<void> {
  try {
    await queryFulfilled;
  } catch {
    // A failed write changed nothing, so the cached summaries still hold.
    return;
  }

  dispatch(
    dataFeedsApi.util.invalidateTags([
      { type: 'DataFeeds', id: feedListId },
      { type: 'HomeInstitutions', id: 'LIST' },
    ]),
  );
}

function assertAdvisorRole(
  state: WARootState,
): FetchBaseQueryError | undefined {
  if (state.auth.currentRole !== Role.Advisor) {
    return {
      status: 403,
      data: 'Setup accounts are only available for advisor role',
    };
  }
  return undefined;
}

function assertFirmRole(state: WARootState): FetchBaseQueryError | undefined {
  if (state.auth.currentRole !== Role.Firm) {
    return {
      status: 403,
      data: 'Setup accounts are only available for firm role',
    };
  }
  return undefined;
}

export const setupAccountsApi = createApi({
  reducerPath: 'setupAccountsApi',
  baseQuery: waServicesQuery,
  tagTypes: ['SetupAccounts'],
  endpoints: (builder) => ({
    getSetupAccounts: builder.query<
      RecordResponse<FirmSetupAccount>,
      FirmSetupAccountsRequest
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertFirmRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/firm/setupAccounts',
          method: 'GET',
          params: buildSetupAccountsQueryParams(args),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<FirmSetupAccount> };
      },
      providesTags: (_result, _error, args) => [
        { type: 'SetupAccounts', id: setupAccountsTagId(args) },
      ],
    }),
    updateSetupAccount: builder.mutation<undefined, FirmSetupAccount>({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertFirmRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: `/api/firm/setupAccounts/${String(arg.WealthAccountId)}`,
          method: 'PUT',
          body: arg,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
      invalidatesTags: [{ type: 'SetupAccounts' }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await refreshFeedSummaries(dispatch, queryFulfilled, 'LIST');
      },
    }),
    bulkEditSetupAccounts: builder.mutation<
      boolean | null,
      AdvisorBulkEditAccountsRequest
    >({
      queryFn: async (body, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertFirmRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/firm/setupAccounts/bulkEdit',
          method: 'PUT',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        // HTTP 200 with false means the server declined the write.
        if (result.data === false) {
          return {
            error: {
              status: 'CUSTOM_ERROR',
              data: { message: 'Bulk edit was not applied' },
              error: 'Bulk edit was not applied',
            } as FetchBaseQueryError,
          };
        }

        return { data: result.data as boolean | null };
      },
      invalidatesTags: [{ type: 'SetupAccounts' }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await refreshFeedSummaries(dispatch, queryFulfilled, 'LIST');
      },
    }),
    postDataFeedActivity: builder.mutation<undefined, DataFeedActivityRequest>({
      queryFn: async (body, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertFirmRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/firm/setupAccounts/DataFeedActivity',
          method: 'POST',
          body,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
    }),
    getAdvisorSetupAccounts: builder.query<
      RecordResponse<SetupAccount>,
      FirmSetupAccountsRequest
    >({
      queryFn: async (args, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/advisor/setupAccounts',
          method: 'GET',
          params: buildSetupAccountsQueryParams(args),
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: result.data as RecordResponse<SetupAccount> };
      },
      providesTags: [{ type: 'SetupAccounts', id: 'LIST' }],
    }),
    updateAdvisorSetupAccount: builder.mutation<undefined, SetupAccount>({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: `/api/advisor/setupAccounts/${String(arg.WealthAccountId)}`,
          method: 'PUT',
          body: arg,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
      invalidatesTags: [{ type: 'SetupAccounts', id: 'LIST' }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await refreshFeedSummaries(dispatch, queryFulfilled, 'ADVISOR_LIST');
      },
    }),
    bulkEditAdvisorAccounts: builder.mutation<
      undefined,
      AdvisorBulkEditAccountsRequest
    >({
      queryFn: async (arg, { getState }, _extraOptions, baseQuery) => {
        const state = getState() as WARootState;
        const roleError = assertAdvisorRole(state);
        if (roleError) {
          return { error: roleError };
        }

        const result = await baseQuery({
          url: '/api/advisor/setupAccounts/bulkEdit',
          method: 'PUT',
          body: arg,
        });

        if (result.error) {
          return { error: result.error };
        }

        return { data: undefined };
      },
      invalidatesTags: [{ type: 'SetupAccounts', id: 'LIST' }],
      onQueryStarted: async (_arg, { dispatch, queryFulfilled }) => {
        await refreshFeedSummaries(dispatch, queryFulfilled, 'ADVISOR_LIST');
      },
    }),
  }),
});

export const {
  useGetSetupAccountsQuery,
  useLazyGetSetupAccountsQuery,
  useUpdateSetupAccountMutation,
  useBulkEditSetupAccountsMutation,
  usePostDataFeedActivityMutation,
  useGetAdvisorSetupAccountsQuery,
  useUpdateAdvisorSetupAccountMutation,
  useBulkEditAdvisorAccountsMutation,
} = setupAccountsApi;
